/**
 * Sector operations — Member Construction Approvals Group (R39 B9.b2).
 * Shows "Waiting on me" group of OPEN HINDRANCE / RFI / SELECTION issues in readable projects.
 * Online-only issue responses.
 */

import React, { useEffect, useMemo, useState, useId, useRef } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { AppDialog, Button, HiddenFileInput, StatusPill, TextInput } from '../../Common'
import { listConProjects } from '../../../services/conProjectsService'
import { listConIssues, respondConIssue } from '../../../services/conIssuesService'
import { uploadConIssuePhoto } from '../../../services/conIssuePhotosService'
import { createOpId } from '../../../services/helper/opId'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import { type ConProject, type ConIssue, MAX_CON_ISSUE_PHOTOS } from '../../../types/ops'
import styles from './MemberConApprovalsGroup.module.css'

export interface MemberConApprovalsGroupProps {
  workspaceId: string
  callerUid: string
  scope: {
    ready: boolean
    restricted: boolean
    allowedPlaceIds?: ReadonlySet<string>
  }
}

const unwrapVal = (v: unknown): string =>
  v != null && typeof v === 'object' && 'target' in v
    ? String((v as { target: { value?: unknown } }).target.value ?? '')
    : String(v ?? '')

export function MemberConApprovalsGroup({
  workspaceId,
  callerUid,
  scope,
}: MemberConApprovalsGroupProps): React.ReactElement {
  const { t } = useNls()
  const responseInputId = useId()

  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  )

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const [projects, setProjects] = useState<ConProject[]>([])
  const [issues, setIssues] = useState<ConIssue[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [refreshNonce, setRefreshNonce] = useState<number>(0)

  // Respond Dialog
  const [selectedIssue, setSelectedIssue] = useState<ConIssue | null>(null)
  const [responseText, setResponseText] = useState<string>('')
  const [respondPhotos, setRespondPhotos] = useState<File[]>([])
  const [actionBusy, setActionBusy] = useState<boolean>(false)
  const respondFileInputRef = useRef<HTMLInputElement>(null)

  // Load readable projects
  useEffect(() => {
    if (!workspaceId || !scope.ready) return
    let active = true

    async function loadProjects() {
      try {
        const res = await listConProjects({ workspaceId })
        if (active) {
          setProjects(res.projects || [])
        }
      } catch (err) {
        if (active) setErrorMsg(getApiErrorMessage(err))
      }
    }

    void loadProjects()
    return () => {
      active = false
    }
  }, [workspaceId, scope.ready])

  const readableProjects = useMemo(() => {
    if (!scope.restricted || !scope.allowedPlaceIds) return projects
    return projects.filter((p) => scope.allowedPlaceIds?.has(p.placeId))
  }, [projects, scope.restricted, scope.allowedPlaceIds])

  // Load issues for all readable projects
  useEffect(() => {
    if (!workspaceId || readableProjects.length === 0) {
      return
    }
    let active = true

    void Promise.resolve().then(async () => {
      if (!active) return
      setLoading(true)
      setErrorMsg(null)
      try {
        let failedProjectCount = 0
        const results = await Promise.all(
          readableProjects.map(async (p) => {
            try {
              return await listConIssues({
                workspaceId,
                projectId: p.id,
                state: 'OPEN',
              })
            } catch {
              failedProjectCount += 1
              return { issues: [] }
            }
          }),
        )
        if (active) {
          const combined = results.flatMap((r) => r.issues || [])
          setIssues(combined)
          if (failedProjectCount > 0) {
            setErrorMsg(
              t('NLSMemberOpsConApprovalsProjectsLoadFailed', {
                count: String(failedProjectCount),
              }),
            )
          }
        }
      } catch (err) {
        if (active) setErrorMsg(getApiErrorMessage(err))
      } finally {
        if (active) setLoading(false)
      }
    })

    return () => {
      active = false
    }
  }, [workspaceId, readableProjects, refreshNonce, t])

  // Filter for OPEN HINDRANCE / RFI / SELECTION where responsibleParty is INTERNAL and uid is callerUid
  const waitingOnMeIssues = useMemo(() => {
    const list = readableProjects.length === 0 ? [] : issues
    return list.filter((iss) => {
      if (iss.state !== 'OPEN') return false
      if (iss.kind !== 'HINDRANCE' && iss.kind !== 'RFI' && iss.kind !== 'SELECTION') {
        return false
      }
      return iss.responsibleParty?.type === 'INTERNAL' && iss.responsibleParty?.uid === callerUid
    })
  }, [issues, readableProjects.length, callerUid])

  const handleOpenRespond = (issue: ConIssue) => {
    setSelectedIssue(issue)
    setResponseText('')
    setRespondPhotos([])
    setErrorMsg(null)
  }

  const handleRespondSubmit = async () => {
    if (!selectedIssue || !isOnline) return
    const trimmed = responseText.trim()
    if (trimmed.length < 1 || trimmed.length > 4000) return
    setActionBusy(true)
    setErrorMsg(null)
    try {
      const uploadedPaths: string[] = []
      for (const file of respondPhotos) {
        const up = await uploadConIssuePhoto(workspaceId, selectedIssue.id, file)
        uploadedPaths.push(up.path)
      }
      await respondConIssue({
        workspaceId,
        issueId: selectedIssue.id,
        text: trimmed,
        photoPaths: uploadedPaths.length > 0 ? uploadedPaths : undefined,
        opId: createOpId('iss_resp'),
      })
      setSelectedIssue(null)
      setResponseText('')
      setRespondPhotos([])
      setRefreshNonce((n) => n + 1)
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setActionBusy(false)
    }
  }

  return (
    <div className={styles.group} data-testid="member-con-approvals-group">
      <div className={styles.groupHeader}>
        <h3 className={styles.groupTitle}>
          {t('NLSMemberOpsConApprovalsTitle')}
        </h3>
      </div>

      {errorMsg && (
        <div role="alert" className={styles.emptyState}>
          {errorMsg}
        </div>
      )}

      {loading ? (
        <div className={styles.emptyState}>{t('NLSCommonStatusLoading')}</div>
      ) : waitingOnMeIssues.length === 0 ? (
        <div className={styles.emptyState}>
          {t('NLSMemberOpsConApprovalsEmpty')}
        </div>
      ) : (
        <ul className={styles.issuesList}>
          {waitingOnMeIssues.map((iss) => {
            const dueText = iss.responseDueAt
              ? new Date(iss.responseDueAt).toLocaleDateString()
              : ''
            return (
              <li
                key={iss.id}
                className={styles.issueCard}
                data-testid={`con-issue-card-${iss.id}`}
              >
                <div className={styles.issueTop}>
                  <h4 className={styles.issueTitle}>{iss.title}</h4>
                  <div className={styles.badgeRow}>
                    <StatusPill tone="warning">{iss.kind}</StatusPill>
                    <StatusPill tone="neutral">{iss.registerNo}</StatusPill>
                  </div>
                </div>
                <div className={styles.issueMeta}>
                  <span>{iss.responsibleParty.name}</span>
                  {dueText && <span>{dueText}</span>}
                </div>
                <div className={styles.issueActions}>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={!isOnline || actionBusy}
                    onClick={() => handleOpenRespond(iss)}
                    data-testid={`con-issue-respond-btn-${iss.id}`}
                  >
                    {t('NLSMemberOpsConApprovalsRespondBtn')}
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {/* Respond Dialog */}
      {selectedIssue && (
        <AppDialog
          open={Boolean(selectedIssue)}
          title={t('NLSMemberOpsConApprovalsDialogTitle')}
          onDismiss={() => setSelectedIssue(null)}
          actions={[
            {
              label: t('NLSCommonActionCancel'),
              variant: 'secondary',
              onClick: () => setSelectedIssue(null),
              disabled: actionBusy,
            },
            {
              label: t('NLSMemberOpsConApprovalsConfirm'),
              variant: 'primary',
              onClick: handleRespondSubmit,
              disabled:
                actionBusy ||
                !isOnline ||
                responseText.trim().length < 1 ||
                responseText.trim().length > 4000,
              testId: `con-issue-confirm-respond-${selectedIssue.id}`,
            },
          ]}
        >
          <div className={styles.dialogForm}>
            <p>
              <strong>{selectedIssue.registerNo}</strong>: {selectedIssue.title}
            </p>
            <div className={styles.dialogField}>
              <label htmlFor={responseInputId} className={styles.dialogLabel}>
                {t('NLSMemberOpsConApprovalsResponseLabel')}
              </label>
              <TextInput
                id={responseInputId}
                data-testid="con-issue-respond-text-input"
                value={responseText}
                onChange={(e) => setResponseText(unwrapVal(e))}
                placeholder={t('NLSMemberOpsConApprovalsResponsePlaceholder')}
              />
            </div>
            <div className={styles.dialogField}>
              <label htmlFor="member-con-issue-respond-photos-btn" className={styles.dialogLabel}>
                {t('NLSOpsConIssuesPhotos')}
              </label>
              <Button
                id="member-con-issue-respond-photos-btn"
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => respondFileInputRef.current?.click()}
                disabled={actionBusy}
              >
                {t('NLSOpsConIssuesAddPhotos')}
              </Button>
              <HiddenFileInput
                ref={respondFileInputRef}
                testId="con-issue-respond-photos-input"
                accept="image/jpeg,image/png,image/webp,image/heic"
                multiple
                disabled={actionBusy}
                onFiles={(fileList) => {
                  const files = Array.from(fileList)
                  setRespondPhotos((prev) => [...prev, ...files].slice(0, MAX_CON_ISSUE_PHOTOS))
                }}
              />
              {respondPhotos.length > 0 && (
                <div className={styles.dialogMeta}>
                  {respondPhotos.length} / {MAX_CON_ISSUE_PHOTOS}
                </div>
              )}
            </div>
          </div>
        </AppDialog>
      )}
    </div>
  )
}
